import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import {
  deleteDrawingRevision,
  listDrawingRevisions,
  openDrawingRevision,
} from "@/features/drawings/api";
import { errorMessage } from "@/lib/api";

/**
 * 図面 PDF の履歴ダイアログ。登録された版を新しい順に並べ、開く・削除ができる。
 *
 * **追加はここでは行わない**。差し替えは明細の見積画面の「図面を更新」から行い、
 * 版はサーバー側で採番する。入口を 2 つに分けないのは、片方で版を手入力すると
 * もう片方の採番とぶつかるため。
 *
 * PDF は図面に紐づく。図面は明細ごとに 1 枚なので、ここに出る版はこの明細だけの
 * 履歴で、同じ図番の別の明細には影響しない。
 */
export default function DrawingRevisionsDialog({
  isOpen,
  drawingId,
  drawingNumber,
  itemName,
  canWrite,
  onClose,
  onChanged,
}: {
  isOpen: boolean;
  /** null のとき（図番なしの行）は開かない。 */
  drawingId: number | null;
  drawingNumber: string | null;
  itemName: string;
  canWrite: boolean;
  onClose: () => void;
  /** 版が増減したときに呼ぶ（一覧の件数を引き直すため）。 */
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setBusy(false);
  }, [isOpen, drawingId]);

  const revisions = useQuery({
    queryKey: ["drawing-revisions", drawingId],
    queryFn: () => listDrawingRevisions(drawingId as number),
    enabled: isOpen && drawingId != null,
  });

  const remove = async (revisionId: number) => {
    if (drawingId == null) return;
    setBusy(true);
    try {
      await deleteDrawingRevision(drawingId, revisionId);
      await revisions.refetch();
      onChanged();
    } catch (e) {
      setError(errorMessage(e, "PDF を削除できませんでした。"));
    } finally {
      setBusy(false);
    }
  };

  const items = revisions.data?.items ?? [];

  return (
    <Modal
      isOpen={isOpen && drawingId != null}
      onOpenChange={(open) => !open && onClose()}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>図面 PDF の履歴</span>
          <span className="text-small font-normal text-default-500">
            {drawingNumber} {itemName}
          </span>
        </ModalHeader>

        <ModalBody>
          {revisions.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner label="読み込み中..." />
            </div>
          ) : items.length === 0 ? (
            <p className="py-2 text-sm text-default-500">
              まだ PDF が登録されていません。
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-default-100">
              {items.map((r) => (
                <li
                  key={r.drawing_revision_id}
                  className="flex items-center gap-3 py-2 text-sm"
                >
                  <span className="w-16 shrink-0 font-bold">版 {r.revision}</span>
                  <button
                    type="button"
                    onClick={() =>
                      void openDrawingRevision(
                        drawingId as number,
                        r.drawing_revision_id,
                      )
                    }
                    aria-label={`版 ${r.revision} を開く`}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 text-left text-brand-500 underline underline-offset-2 hover:text-brand-600"
                  >
                    <FileText size={14} className="shrink-0" />
                    <span className="truncate">
                      {r.original_filename ?? "図面.pdf"}
                    </span>
                  </button>
                  <span className="shrink-0 text-tiny text-default-400">
                    {r.created_at?.slice(0, 10)}
                  </span>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    aria-label={`版 ${r.revision} を削除`}
                    isDisabled={!canWrite || busy}
                    onPress={() => void remove(r.drawing_revision_id)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          <Button radius="full" variant="light" data-testid="close" onPress={onClose}>
            閉じる
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
